/**
 * API Expose Dashboard
 *
 * Agent-driven layout: workflow runner on top, management tabs below.
 * Follows the IoT Shack / API Ingest pattern.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Box, Paper, Tabs, Tab, Typography } from '@mui/material';
import {
  People as PeopleIcon,
  Hub as McpIcon,
  MenuBook as DocsIcon,
  Storage as DataSourcesIcon,
  MonitorHeart as HealthIcon,
} from '@mui/icons-material';

import ApiExposeChat from './ApiExposeChat';
import ConsumersTab from './ConsumersTab';
import DataSourcesTab from './DataSourcesTab';
import MintTokenTab from './MintTokenTab';
import McpEndpointsTab from './McpEndpointsTab';
import TrekkerDemoTab from './TrekkerDemoTab';
import ExposeHealthTab from './health/ExposeHealthTab';
import AskAIPanel from '@/components/AskAIPanel';
import PageShell from '@/components/PageShell';
import { ArrowUpFromLine } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`api-expose-tabpanel-${index}`}
      aria-labelledby={`api-expose-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

export default function ApiExposeDashboard() {
  const [searchParams] = useSearchParams();
  const requestedTab = useMemo(() => {
    const tab = String(searchParams.get('tab') || '').trim().toLowerCase();
    if (tab === 'health' || tab === 'monitor') return 0;
    if (tab === 'consumers') return 1;
    if (tab === 'data-sources' || tab === 'data_sources') return 2;
    if (tab === 'mcp' || tab === 'mcp-endpoints') return 3;
    if (tab === 'docs' || tab === 'docs-demos') return 4;
    return 0;
  }, [searchParams]);
  const initialAppId = String(searchParams.get('app_id') || '').trim();
  const [activeTab, setActiveTab] = useState(requestedTab);

  useEffect(() => {
    setActiveTab(requestedTab);
  }, [requestedTab]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => setActiveTab(newValue);

  return (
    <PageShell
      title="API Expose"
      subtitle="Manage data sources, API consumers, MCP endpoints, tokens, and scopes for third-party app access"
      icon={ArrowUpFromLine}
      iconColor="bg-purple-600"
    >

      {/* Primary split-panel: chat left, live view right */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '3fr 2fr' }, gap: 2, mb: 2 }}>
        <ApiExposeChat />
        <Paper sx={{ width: '100%' }}>
          <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="subtitle2" color="text.secondary">
              Live View
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Quick visibility into current MCP endpoints (full management below).
            </Typography>
          </Box>
          <Box sx={{ p: 1.5 }}>
            <McpEndpointsTab compact />
          </Box>
        </Paper>
      </Box>

      {/* Management Tabs */}
      <Paper sx={{ width: '100%' }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="API Expose tabs"
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
        >
          <Tab icon={<HealthIcon />} iconPosition="start" label="Health" id="api-expose-tab-0" aria-controls="api-expose-tabpanel-0" />
          <Tab icon={<PeopleIcon />} iconPosition="start" label="Consumers" id="api-expose-tab-1" aria-controls="api-expose-tabpanel-1" />
          <Tab icon={<DataSourcesIcon />} iconPosition="start" label="Data Sources" id="api-expose-tab-2" aria-controls="api-expose-tabpanel-2" />
          <Tab icon={<McpIcon />} iconPosition="start" label="MCP Endpoints" id="api-expose-tab-3" aria-controls="api-expose-tabpanel-3" />
          <Tab icon={<DocsIcon />} iconPosition="start" label="Docs & Demos" id="api-expose-tab-4" aria-controls="api-expose-tabpanel-4" />
        </Tabs>

        <TabPanel value={activeTab} index={0}><ExposeHealthTab /></TabPanel>
        <TabPanel value={activeTab} index={1}>
          <ConsumersTab />
          <Box sx={{ mt: 2 }}>
            <MintTokenTab />
          </Box>
        </TabPanel>
        <TabPanel value={activeTab} index={2}><DataSourcesTab initialAppId={initialAppId} /></TabPanel>
        <TabPanel value={activeTab} index={3}><McpEndpointsTab /></TabPanel>
        <TabPanel value={activeTab} index={4}><TrekkerDemoTab /></TabPanel>
      </Paper>

      <AskAIPanel
        domain="apis"
        title="API Expose AI"
        placeholder="Ask about app auth, scopes, Kong routes, or using /public/*..."
        initialMessage="Hi! I can help you expose DevCockpit to 3rd-party apps safely (scopes, JWTs, Kong). What are you building?"
      />
    </PageShell>
  );
}
